/**
 * bandit.ts — the reflex agent: a tree-ensemble contextual bandit with
 * two-timescale memory, ε-floor cold start, and EWMA drift detection.
 *
 * Ported from `ReflexBandit` in `_proto_reflex_v1.py` (lines 416-596). The
 * select→observe loop is the bandit's core:
 *
 *     X = env.candidateFeatures()           # 8 actions × 83 features
 *     a = agent.select(X)                   # three exploration layers (below)
 *     {r, done} = env.step(a)               # immediate bandit reward
 *     agent.observe(X[a], a, r)             # add to buffers, feed drift, refit
 *
 * THREE LAYERS OF EXPLORATION, stacked (select):
 *   1. DRIFT re-explore window (highest priority): if the drift detector
 *      recently fired, act uniform random for `exploreWindow` actions
 *      regardless of mode. This is the H3 mechanism — fresh data after the
 *      world changes shape.
 *   2. ε-FLOOR cold start: uniform random until n_observed >= cold_start_k.
 *   3. MODE-DRIVEN: TS (Thompson sample one tree per action) / greedy (mean
 *      argmax) / random (ignore model).
 *
 * TWO-TIMESCALE MEMORY (the v1→v2 fix):
 *   - LONG memory (LONG_CAP, never reset): carries token SEMANTICS across
 *     days. "GOLD is good" survives a day boundary.
 *   - DAY memory (WINDOW_PER_DAY, reset per day): carries the current day's
 *     SHAPE pattern. Yesterday's arrangement is forgotten.
 *   At fit time we concatenate [day] + [long]; the tree sees today's shape
 *     AND the cross-day token meaning.
 *
 * Fit cadence: every REFIT_EVERY observations, the forest refits on the
 * combined buffer. Fit happens ASYNC via a Web Worker (see forest_client.ts);
 * the agent keeps predicting with the stale forest until the fresh one
 * returns, then swaps it in.
 */

import {
  N_ACTIONS,
  N_FEATURES,
  WINDOW_PER_DAY,
  LONG_CAP,
  COLD_START_K,
  REFIT_EVERY,
  type ArmMode,
  type DriftStatus,
} from './constants';
import { ExtraTreesForest } from './forest';
import { EWMADriftDetector } from './drift';
import type { DriftHyperparams } from './drift';
import { makeRng } from './linalg';

export interface BanditHyperparams {
  mode: ArmMode;
  nTrees: number;
  minSamplesLeaf: number;
  windowPerDay: number;
  longCap: number;
  coldStartK: number;
  refitEvery: number;
  useDriftDetector: boolean;
  drift: DriftHyperparams;
  seed: number;
}

export const DEFAULT_BANDIT_HP: BanditHyperparams = {
  mode: 'ts',
  nTrees: 40,
  minSamplesLeaf: 10,
  windowPerDay: WINDOW_PER_DAY,
  longCap: LONG_CAP,
  coldStartK: COLD_START_K,
  refitEvery: REFIT_EVERY,
  useDriftDetector: true,
  drift: { lam: 0.05, lWarning: 2.0, lDrift: 3.0, warmup: 50, exploreWindow: 24 },
  seed: 0,
};

/**
 * The result of one select() — the action plus the model's prediction
 * snapshot, surfaced to the HUD / harness for diagnostics.
 */
export interface SelectResult {
  action: number;
  /** Ensemble mean prediction per action (length N_ACTIONS), or zeros if
   *  cold-start / random / pre-fit. */
  mean: Float64Array;
  /** Across-tree std per action (the disagreement exploration signal). */
  std: Float64Array;
  /** Why the action was chosen: cold | drift | model. */
  reason: 'cold' | 'drift' | 'model';
}

export interface ObserveResult {
  refitRequested: boolean; // true if this observation crossed the refit cadence
  driftStatus: 'warmup' | 'ok' | 'warning' | 'drift' | 'off';
}

/**
 * Optional async-fit hook. The main-thread bandit sets this to a function
 * that posts the fit job to the worker; the harness (which runs entirely
 * inside the worker) sets it to a synchronous fitter. This keeps the bandit
 * agnostic to where the fit happens.
 */
export type FitFn = (
  X: Float64Array,
  y: Float64Array,
  hp: BanditHyperparams,
) => void;

export class ReflexBandit {
  hp: BanditHyperparams;
  forest: ExtraTreesForest;
  drift: EWMADriftDetector;
  private rng: () => number;

  // Two-timescale memory buffers. Stored as plain number[] of feature rows
  // (each row is Float64Array of length N_FEATURES) — the per-fit concat
  // materialises them into a flat Float64Array for the forest.
  private XDay: Float64Array[] = [];
  private yDay: number[] = [];
  private XLong: Float64Array[] = [];
  private yLong: number[] = [];

  nObserved = 0; // total lifetime transitions (never resets)
  private lastPred = 0; // prediction at the last chosen action (for drift)
  /**
   * Committed heading for traversal. When the model has no signal on the
   * current window (argmax ≤ 0 — typically empty space, all-FREE window),
   * the agent keeps stepping in `committedDir` so it TRAVELS instead of
   * jittering in place. Cleared the moment argmax > 0 (gold in sight) so
   * the model can take over and exploit. Also cleared/reset when the
   * committed direction is masked out (wall/edge ahead) — see `select`.
   */
  private committedDir: number | null = null;
  /** Diagnostic log of drift events (lifetime, never auto-cleared). */
  driftEvents: Array<{ n: number; status: DriftStatus; err: number }> = [];

  /** Day-buffer occupancy (for HUD display). */
  get dayBufferSize(): number { return this.XDay.length; }
  /** Long-buffer occupancy (for HUD display). */
  get longBufferSize(): number { return this.XLong.length; }

  /** Fit hook — set by the runtime (worker-async in the UI, sync in harness). */
  fitFn: FitFn;

  constructor(hp: Partial<BanditHyperparams> = {}) {
    this.hp = { ...DEFAULT_BANDIT_HP, ...hp };
    this.forest = new ExtraTreesForest({
      nTrees: this.hp.nTrees,
      minSamplesLeaf: this.hp.minSamplesLeaf,
      nFeatures: N_FEATURES,
      seed: this.hp.seed,
    });
    this.drift = new EWMADriftDetector(this.hp.drift);
    this.rng = makeRng(this.hp.seed ^ 0x9e3779b9);
    // Default fit hook: synchronous (used by smoketests + harness). The UI
    // main-thread constructor overrides this to post to the worker.
    this.fitFn = (X, y, h) => {
      this.forest.hp.nTrees = h.nTrees;
      this.forest.hp.minSamplesLeaf = h.minSamplesLeaf;
      this.forest.hp.seed = h.seed;
      this.forest.fitSync(X, y);
    };
  }

  /** Forget the day's SHAPE pattern, KEEP the long-term SEMANTICS.
   *  The drift detector is intentionally NOT reset — it needs continuity
   *  across the boundary to detect drift AT the boundary. */
  resetDay(): void {
    this.XDay = [];
    this.yDay = [];
    // Keep committedDir across a day boundary — a traversal in progress
    // shouldn't be cut just because the day counter rolled.
  }

  /** Full reset between seeds (fresh detector, fresh buffers, fresh forest). */
  resetRun(): void {
    this.XDay = [];
    this.yDay = [];
    this.XLong = [];
    this.yLong = [];
    this.nObserved = 0;
    this.lastPred = 0;
    this.committedDir = null;
    this.driftEvents = [];
    this.drift.resetRun();
    this.forest = new ExtraTreesForest({
      nTrees: this.hp.nTrees,
      minSamplesLeaf: this.hp.minSamplesLeaf,
      nFeatures: N_FEATURES,
      seed: this.hp.seed,
    });
  }

  /** Concatenate [day buffer] + [long buffer] into flat row-major arrays. */
  combinedDataset(): { X: Float64Array; y: Float64Array } {
    const nDay = this.XDay.length;
    const nLong = this.XLong.length;
    const n = nDay + nLong;
    const X = new Float64Array(n * N_FEATURES);
    const y = new Float64Array(n);
    for (let i = 0; i < nDay; i++) {
      X.set(this.XDay[i], i * N_FEATURES);
      y[i] = this.yDay[i];
    }
    for (let i = 0; i < nLong; i++) {
      X.set(this.XLong[i], (nDay + i) * N_FEATURES);
      y[nDay + i] = this.yLong[i];
    }
    return { X, y };
  }

  /**
   * Pick an action. X_all: Float64Array of length N_ACTIONS * N_FEATURES.
   *
   * SELECTION ORDER (four layers):
   *
   *   1. HEADING-COMMIT (highest priority): the model's argmax over the
   *      current window. If argmax > 0 (gold or other signal in sight),
   *      the model drives — clear any committed heading and exploit. If
   *      argmax ≤ 0 (no signal — empty ground), the agent HOLDS its
   *      committed heading so it travels instead of jittering in place.
   *      This applies to ALL modes (incl. cold start, drift re-explore,
   *      random) because "stuck on empty space" is the failure mode it
   *      fixes. When the committed heading is masked out (wall/edge), a
   *      fresh valid heading is picked, reverse-guarded.
   *
   *   2. DRIFT re-explore window (after signal check).
   *   3. ε-FLOOR cold start.
   *   4. MODE-DRIVEN: TS / greedy / random.
   *
   * `mask` (Uint8Array, 1=valid): in-bounds AND not a known stone. The
   * heading-commit consults the mask so a wall-blocked direction is never
   * held; the mode-driven argmax is restricted to valid actions.
   */
  select(X_all: Float64Array, mask?: Uint8Array): SelectResult {
    const valid = mask ? pickValid(mask) : null;

    // ── Signal check ──────────────────────────────────────────────────
    // Predictions are computed only when the forest is fit; before that,
    // there is no model signal by definition (cold start path below).
    let mean: Float64Array = new Float64Array(N_ACTIONS);
    let std: Float64Array = new Float64Array(N_ACTIONS);
    std.fill(1);
    let hasSignal = false;
    if (this.forest.isFit) {
      const p = this.forest.predictAllActions(X_all, N_ACTIONS);
      mean = p.mean; std = p.std;
      // Signal = argmax over VALID actions > 0. On empty ground every
      // prediction is ~0, so hasSignal is false and we fall through to
      // the heading-commit.
      if (valid === null) {
        for (let a = 0; a < N_ACTIONS; a++) {
          if (mean[a] > 0) { hasSignal = true; break; }
        }
      } else {
        for (const a of valid) {
          if (mean[a] > 0) { hasSignal = true; break; }
        }
      }
    }

    // ── Layer 1: heading-commit ───────────────────────────────────────
    if (!hasSignal) {
      // No gold in sight — traverse. Hold the committed heading if it is
      // still valid; otherwise pick a fresh one (reverse-guarded against
      // the heading that just got blocked, so the agent doesn't bounce
      // straight back into the wall).
      const blocked = this.committedDir;
      const stillValid = this.committedDir !== null
        && (valid === null ? false : valid.includes(this.committedDir));
      if (!stillValid) {
        this.committedDir = pickHeading(valid, blocked, this.rng);
      }
      // After the above, committedDir is non-null: either it was kept
      // (stillValid) or freshly assigned by pickHeading.
      const action: number = this.committedDir!;
      this.lastPred = 0;
      const z = new Float64Array(N_ACTIONS);
      z.fill(0);
      return { action, mean: z, std, reason: this.reasonLabel() };
    }

    // Signal present — exploit, clear the commitment.
    this.committedDir = null;

    // Layer 2: drift re-explore window.
    if (this.hp.mode !== 'random' && this.hp.useDriftDetector && this.drift.shouldExplore()) {
      this.lastPred = 0;
      return this.randomSelect('drift', mask);
    }
    // Layer 3: cold start.
    if (this.hp.mode === 'random' || this.nObserved < this.hp.coldStartK) {
      this.lastPred = 0;
      return this.randomSelect('cold', mask);
    }
    // Layer 4: model-driven.
    if (this.hp.mode === 'greedy') {
      const bestA = argmaxOver(mean, valid, this.rng);
      this.lastPred = mean[bestA];
      return { action: bestA, mean, std, reason: 'model' };
    }
    // TS: sample one tree's prediction per action, act on the sample.
    const treePicks = new Int32Array(N_ACTIONS);
    for (let a = 0; a < N_ACTIONS; a++) {
      treePicks[a] = Math.floor(this.rng() * this.hp.nTrees);
    }
    const samples = this.forest.sampleOneTreePrediction(X_all, N_ACTIONS, treePicks);
    const bestA = argmaxOver(samples, valid, this.rng);
    // The "prediction" we track for drift is the ENSEMBLE MEAN at the chosen
    // action (the model's expected reward), not the TS sample. Using the
    // sample would conflate exploration noise with drift.
    this.lastPred = mean[bestA];
    return { action: bestA, mean, std, reason: 'model' };
  }

  /** Reason label for the HUD when the heading-commit layer took over. */
  private reasonLabel(): 'cold' | 'drift' | 'model' {
    if (this.hp.mode === 'random') return 'cold';
    if (this.nObserved < this.hp.coldStartK || !this.forest.isFit) return 'cold';
    if (this.hp.useDriftDetector && this.drift.exploreRemaining > 0) return 'drift';
    return 'model';
  }

  private randomSelect(reason: 'cold' | 'drift', mask?: Uint8Array): SelectResult {
    const mean = new Float64Array(N_ACTIONS);
    const std = new Float64Array(N_ACTIONS);
    std.fill(1);
    const valid = mask ? pickValid(mask) : null;
    const action = valid && valid.length > 0
      ? valid[Math.floor(this.rng() * valid.length)]
      : Math.floor(this.rng() * N_ACTIONS);
    return { action, mean, std, reason };
  }

  /** Add transition to BOTH buffers, feed drift, request refit if due. */
  observe(x: Float64Array, action: number, r: number): ObserveResult {
    void action;
    // Copy the row — the feature buffer is reused next step.
    const xCopy = new Float64Array(N_FEATURES);
    xCopy.set(x);
    this.XDay.push(xCopy);
    this.yDay.push(r);
    this.XLong.push(xCopy);
    this.yLong.push(r);
    this.nObserved++;

    let driftStatus: ObserveResult['driftStatus'] = 'off';
    if (this.hp.useDriftDetector && this.hp.mode !== 'random' && this.lastPred !== 0) {
      const status = this.drift.update(this.lastPred, r);
      driftStatus = status;
      if (status === 'warning' || status === 'drift') {
        this.driftEvents.push({ n: this.nObserved, status, err: Math.abs(r - this.lastPred) });
      }
    }
    this.lastPred = 0;

    // Cap buffers (FIFO).
    if (this.XDay.length > this.hp.windowPerDay) {
      const overflow = this.XDay.length - this.hp.windowPerDay;
      this.XDay.splice(0, overflow);
      this.yDay.splice(0, overflow);
    }
    if (this.XLong.length > this.hp.longCap) {
      const overflow = this.XLong.length - this.hp.longCap;
      this.XLong.splice(0, overflow);
      this.yLong.splice(0, overflow);
    }

    const refitRequested = this.nObserved % this.hp.refitEvery === 0;
    if (refitRequested) {
      const { X, y } = this.combinedDataset();
      if (X.length >= N_ACTIONS * N_FEATURES) {
        this.fitFn(X, y, this.hp);
      }
    }
    return { refitRequested, driftStatus };
  }

  /** For diagnostics: mean across-tree std over candidate actions. */
  lastDisagreement(X_all: Float64Array): number {
    if (!this.forest.isFit) return 1;
    const { std } = this.forest.predictAllActions(X_all, N_ACTIONS);
    let s = 0;
    for (let a = 0; a < N_ACTIONS; a++) s += std[a];
    return s / N_ACTIONS;
  }
}

/**
 * Return the indices where mask is non-zero, or null if none. null (not
 * empty array) is the sentinel the caller uses to mean "no constraint" —
 * it lets argmax/random fall back to the full action set when the agent
 * is somehow boxed in by undiscovered stones (mask can't see those).
 */
function pickValid(mask: Uint8Array): number[] | null {
  const out: number[] = [];
  for (let a = 0; a < mask.length; a++) if (mask[a] !== 0) out.push(a);
  return out.length > 0 ? out : null;
}

/**
 * argmax over `values`, restricted to `valid` indices when non-null.
 * TIES BREAK RANDOMLY: when several actions are within `TIE_EPS` of the top
 * (the degenerate case where the forest has no preference — typically all
 * zeros on empty space, or all-identical on a symmetric window), one is
 * chosen uniformly at random. Without this the agent deterministically
 * picks the lowest-index tied action every tick, which on an empty grid
 * becomes "always march east", and against a wall becomes "ping-pong
 * between the two lowest-index valid actions" — the visible stuck loop.
 */
const TIE_EPS = 1e-9;
function argmaxOver(values: Float64Array, valid: number[] | null, rng: () => number): number {
  const idxs = valid === null
    ? Array.from({ length: values.length }, (_, i) => i)
    : valid;
  if (idxs.length === 0) return 0;
  let best = -Infinity;
  for (const a of idxs) if (values[a] > best) best = values[a];
  // Collect everyone tied with the max within TIE_EPS, pick one at random.
  const tied: number[] = [];
  for (const a of idxs) {
    if (best - values[a] <= TIE_EPS) tied.push(a);
  }
  return tied[Math.floor(rng() * tied.length)];
}

/**
 * Direction pairs (index i ↔ index i^1 are reverses): E/W, S/N, NE/NW, SE/SW.
 * Bit-flip of bit 0 maps a direction index to its exact reverse.
 */
function reverseDir(a: number): number { return a ^ 1; }

/**
 * Pick a fresh heading for the heading-commit layer. Selects uniformly from
 * the valid directions, EXCLUDING the exact reverse of `exclude` (the heading
 * that just got wall-blocked) so the agent doesn't bounce straight back into
 * the wall it just left. Falls back to including the reverse when it is the
 * only valid move (narrow corridor / boxed-in). When `valid` is null (no mask
 * supplied) any of the 8 directions is fair game.
 */
function pickHeading(valid: number[] | null, exclude: number | null, rng: () => number): number {
  if (valid === null || valid.length === 0) {
    return Math.floor(rng() * N_ACTIONS);
  }
  const reverse = exclude === null ? -1 : reverseDir(exclude);
  const candidates = valid.filter((a) => a !== reverse);
  const pool = candidates.length > 0 ? candidates : valid;
  return pool[Math.floor(rng() * pool.length)];
}
